import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Loader2, XCircle, ArrowRight, ExternalLink } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const SKOOL_URL = "https://www.skool.com/perimenopause-decoded-78231";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [skoolCountdown, setSkoolCountdown] = useState(6);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = response.data;
        setPaymentData(data);

        if (data.payment_status === "paid") {
          setStatus("success");
        } else if (data.status === "expired") {
          setStatus("expired");
        } else if (pollCount < 5) {
          // Continue polling
          setTimeout(() => {
            setPollCount((prev) => prev + 1);
          }, 2000);
        } else {
          setStatus("pending");
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
        if (pollCount < 5) {
          setTimeout(() => {
            setPollCount((prev) => prev + 1);
          }, 2000);
        } else {
          setStatus("error");
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, pollCount]);

  const getPackageName = () => {
    if (paymentData?.metadata?.package_name) {
      return paymentData.metadata.package_name;
    }
    return "Your Program";
  };

  const isDecoded = paymentData?.metadata?.package_id === "decoded_monthly";

  // Auto-redirect Decoded buyers to Skool after payment confirmation
  useEffect(() => {
    if (status !== "success" || !isDecoded) return;
    if (skoolCountdown <= 0) {
      window.location.href = SKOOL_URL;
      return;
    }
    const timer = setTimeout(() => setSkoolCountdown((n) => n - 1), 1000);
    return () => clearTimeout(timer);
  }, [status, isDecoded, skoolCountdown]);

  return (
    <div
      data-testid="payment-success-page"
      className="min-h-screen flex items-center justify-center px-6 pt-24"
    >
      <div className="max-w-lg w-full text-center">
        {status === "loading" && (
          <div className="animate-fade-in">
            <Loader2 className="w-16 h-16 text-[#C5A059] mx-auto animate-spin mb-6" />
            <h1 className="font-heading text-3xl text-[#2C3524] mb-4">
              Processing Your Payment
            </h1>
            <p className="text-[#4A5543]">
              Please wait while we confirm your purchase...
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="animate-scale-in">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl text-[#2C3524] mb-4">
              Welcome to Your Transformation!
            </h1>
            <p className="text-[#4A5543] text-lg mb-2">
              Your purchase of <strong>{getPackageName()}</strong> is complete.
            </p>

            {isDecoded ? (
              <>
                <p className="text-[#4A5543] mb-8">
                  Redirecting you to the Perimenopause Decoded Skool community in{" "}
                  <span className="font-heading text-[#C5A059] text-xl">
                    {skoolCountdown}
                  </span>
                  …
                </p>
                <a
                  href={SKOOL_URL}
                  className="btn-primary group inline-flex items-center gap-2"
                  data-testid="skool-enter-btn"
                >
                  Enter the Community Now
                  <ExternalLink className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
                <p className="mt-6 text-xs text-[#4A5543]/70">
                  We've also emailed your welcome details and access link.
                </p>
              </>
            ) : (
              <>
                <p className="text-[#4A5543] mb-8">
                  Check your email for access instructions and next steps.
                </p>
                <div className="card p-6 mb-8 text-left">
                  <h3 className="font-heading text-xl text-[#2C3524] mb-4">
                    What happens next?
                  </h3>
                  <ul className="space-y-3 text-[#4A5543]">
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#C5A059] text-white flex items-center justify-center flex-shrink-0 text-sm">
                        1
                      </span>
                      Check your inbox for your welcome email
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#C5A059] text-white flex items-center justify-center flex-shrink-0 text-sm">
                        2
                      </span>
                      Join our Skool community
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#C5A059] text-white flex items-center justify-center flex-shrink-0 text-sm">
                        3
                      </span>
                      Begin your transformation journey
                    </li>
                  </ul>
                </div>
                <Link to="/" className="btn-primary group">
                  Return Home
                  <ArrowRight className="inline-block ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </>
            )}
          </div>
        )}

        {status === "pending" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-12 h-12 text-yellow-600" />
            </div>
            <h1 className="font-heading text-3xl text-[#2C3524] mb-4">
              Payment Processing
            </h1>
            <p className="text-[#4A5543] mb-8">
              Your payment is still being processed. You'll receive an email
              confirmation once complete.
            </p>
            <Link to="/" className="btn-secondary">
              Return Home
            </Link>
          </div>
        )}

        {(status === "error" || status === "expired") && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h1 className="font-heading text-3xl text-[#2C3524] mb-4">
              {status === "expired" ? "Session Expired" : "Something Went Wrong"}
            </h1>
            <p className="text-[#4A5543] mb-8">
              {status === "expired"
                ? "Your payment session has expired. Please try again."
                : "We couldn't confirm your payment. Please try again or contact support."}
            </p>
            <Link to="/#programs" className="btn-primary">
              View Programs
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
